import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { Check2, CheckCircle, Circle, X } from 'react-bootstrap-icons';
import { useAuth } from '@/contexts/AuthContext';
import ArcanumLogo from '@/assets/arc_logo_black.svg?react';
// The signed-out screens share one look. It lives beside the sign-in form.
import '@/components/LoginForm.css';

/** What a password needs. `message` finishes the sentence "Password must contain ...". */
const REQUIREMENTS = [
  { label: 'At least 8 characters', message: 'at least 8 characters', pattern: /.{8,}/ },
  { label: 'A lowercase letter', message: 'at least one lowercase letter', pattern: /[a-z]/ },
  { label: 'An uppercase letter', message: 'at least one uppercase letter', pattern: /[A-Z]/ },
  { label: 'A number', message: 'at least one number', pattern: /[0-9]/ },
  {
    label: 'A symbol, such as ! @ # $ % ^ & *',
    message: 'at least one symbol (!@#$%^&*)',
    pattern: /[!@#$%^&*(),.?":{}|<>]/,
  },
];

export default function CreatePassword() {
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** The requirements the typed password does not meet. Null before anything is typed. */
  const [passwordErrors, setPasswordErrors] = useState<string[] | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requestPasswordReset, confirmPasswordReset } = useAuth();

  // Check for email, code, and mode in URL parameters
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    const codeFromUrl = searchParams.get('code');

    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
    if (codeFromUrl) {
      setResetCode(codeFromUrl);
      setIsCodeSent(true);
    }
  }, [searchParams]);

  // Determine if this is reset mode or create mode
  const isResetMode = searchParams.get('mode') === 'reset';
  const pageTitle = isResetMode ? 'Reset your password' : 'Create your password';
  const codePrompt = isResetMode
    ? 'Enter your email to receive a password reset code.'
    : 'Enter your email to receive an activation code.';
  const codeInstructions = isResetMode
    ? 'Enter the reset code and create your new password.'
    : 'Enter the activation code and create your password.';
  const buttonText = isResetMode ? 'Send reset code' : 'Send activation code';
  const submitText = isResetMode ? 'Reset password' : 'Create password';
  const loadingText = isResetMode ? 'Resetting password' : 'Creating password';
  const successMessage = isResetMode
    ? 'Password reset. Taking you to sign in.'
    : 'Password created. Taking you to sign in.';

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await requestPasswordReset(email.toLowerCase());
      setIsCodeSent(true);
      const successMsg = isResetMode
        ? 'Reset code sent. Check your email and enter the code below.'
        : 'Activation code sent. Check your email and enter the code below.';
      setSuccess(successMsg);
    } catch (err: unknown) {
      const errorMsg = isResetMode
        ? err instanceof Error
          ? err.message
          : 'The reset code was not sent.'
        : err instanceof Error
          ? err.message
          : 'The activation code was not sent.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (passwordErrors && passwordErrors.length > 0) {
      setError('The password does not meet every requirement yet.');
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(email.toLowerCase(), resetCode, newPassword);
      setSuccess(successMessage);
      setTimeout(() => navigate('/'), 3000);
    } catch (err: unknown) {
      const errorMsg = isResetMode
        ? err instanceof Error
          ? err.message
          : 'The password was not reset.'
        : err instanceof Error
          ? err.message
          : 'The password was not created.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password: string) => {
    const errors = REQUIREMENTS.filter((req) => !password.match(req.pattern)).map((req) => req.message);

    setPasswordErrors(errors.length > 0 ? errors : null);
  };

  const handleNewPasswordChange = (password: string) => {
    setNewPassword(password);
    validatePassword(password);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card__head">
          <ArcanumLogo className="login-logo auth-logo" />
          <h1 className="auth-title">{pageTitle}</h1>
          <p className="auth-step">{!isCodeSent ? codePrompt : codeInstructions}</p>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="auth-success">
            <CheckCircle />
            {success}
          </Alert>
        )}

        {!isCodeSent ? (
          <Form onSubmit={handleRequestCode}>
            <Form.Group className="mb-3" controlId="auth-create-email">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <Form.Text>
                {isResetMode
                  ? 'Enter the email address associated with your account.'
                  : 'This should be the email address your administrator used when creating your account.'}
              </Form.Text>
            </Form.Group>

            <Button variant="primary" type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Sending' : buttonText}
            </Button>

            <Button variant="link" className="auth-link" onClick={() => navigate('/')}>
              Back to sign in
            </Button>
          </Form>
        ) : (
          <Form onSubmit={handleCreatePassword}>
            <Form.Group className="mb-3" controlId="auth-create-email-shown">
              <Form.Label>Email address</Form.Label>
              <Form.Control type="email" value={email} readOnly disabled autoComplete="email" />
            </Form.Group>

            <Form.Group className="mb-3" controlId="auth-create-code">
              <Form.Label>{isResetMode ? 'Reset code' : 'Activation code'}</Form.Label>
              <Form.Control
                type="text"
                placeholder="The code from your email"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                autoComplete="off"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="auth-create-new">
              <Form.Label>New password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => handleNewPasswordChange(e.target.value)}
                required
                autoComplete="new-password"
                aria-describedby="auth-create-rules"
              />
              {/* Each requirement, ticked as soon as the typed password meets it. */}
              <ul className="auth-rules" id="auth-create-rules">
                {REQUIREMENTS.map((req) => {
                  const met = newPassword !== '' && req.pattern.test(newPassword);
                  const missing = passwordErrors !== null && passwordErrors.includes(req.message);
                  return (
                    <li key={req.message} className={met ? 'is-met' : missing ? 'is-missing' : undefined}>
                      {met ? (
                        <Check2 aria-label="Met" />
                      ) : missing ? (
                        <X aria-label="Not met yet" />
                      ) : (
                        <Circle size={9} aria-hidden="true" />
                      )}
                      {req.label}
                    </li>
                  );
                })}
              </ul>
            </Form.Group>

            <Form.Group className="mb-3" controlId="auth-create-confirm">
              <Form.Label>Confirm new password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="auth-submit"
              disabled={loading || Boolean(passwordErrors && passwordErrors.length > 0)}
            >
              {loading ? loadingText : submitText}
            </Button>

            <Button variant="link" className="auth-link" onClick={() => setIsCodeSent(false)}>
              Use a different email
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
}
